import { z } from 'zod';
import './schemas'; // polskie komunikaty zod

/**
 * Strefa klienta (docs/18): strefa kartoteki klienta — wszystkie sprawy, w których jest klientem: harmonogram prac
 * (kalendarz), umowy, oferty, protokoły i faktury. Wejście kodem z e-maila; urządzenie zaufane przez PORTAL_TRUST_DAYS dni.
 */

export const PORTAL_TRUST_DAYS = 30;

/** Stan pracy widziany przez klienta. */
export const PORTAL_WORK_STATES = {
  zaplanowane: 'Zaplanowane',
  w_toku: 'W toku',
  wstrzymane: 'Wstrzymane',
  zakonczone: 'Zakończone',
} as const;
export type PortalWorkState = keyof typeof PORTAL_WORK_STATES;

/** Jedna pozycja harmonogramu: odcinek pracy (dni od–do). Bez osób, godzin i opisu dla ekipy. */
export interface PortalWorkDto {
  id: string;
  /** sprawa pracy (filtr „Sprawa” w strefie) */
  caseId: string;
  /** czego dotyczy praca (tytuł zadania) */
  title: string;
  /** branże zadania (np. „Instalacja elektryczna”) */
  trades: string[];
  dateFrom: string;
  dateTo: string;
  skipWeekends: boolean;
  state: PortalWorkState;
  /** praca z przerwami: który to etap (np. 2 z 3); null — jeden termin */
  part: { no: number; of: number } | null;
  /** zadanie, którego dotyczy pozycja (prośba o zmianę terminu dotyczy całej pracy, także wszystkich etapów) */
  taskId: string;
  /** „Poproś o zmianę terminu” — praca zaplanowana od jutra, bez otwartej prośby (docs/18 §Zmiana terminu) */
  canRequestChange: boolean;
  /** ostatnia prośba klienta o zmianę terminu tej pracy */
  changeRequest: PortalChangeRequestDto | null;
}

/** Prośba klienta o zmianę terminu pracy: otwarta → termin przesunięty albo odpowiedź koordynatora (bez zmiany). */
export const CHANGE_REQUEST_STATES = { otwarta: 'Prośba wysłana', przesunieta: 'Termin zmieniony', odpowiedz: 'Odpowiedź biura' } as const;
export type ChangeRequestState = keyof typeof CHANGE_REQUEST_STATES;

export interface PortalChangeRequestDto {
  id: string;
  status: ChangeRequestState;
  /** treść prośby klienta (np. „wypadł mi wyjazd, proszę o przesunięcie o tydzień”) */
  note: string;
  /** odpowiedź koordynatora, gdy termin został bez zmian */
  answer: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export const CHANGE_REQUEST_NOTE_MAX = 1000;
export const portalChangeRequestSchema = z.object({
  taskId: z.string().uuid(),
  note: z.string().trim().min(3, 'Napisz, na kiedy chcesz przesunąć termin').max(CHANGE_REQUEST_NOTE_MAX),
});
export type PortalChangeRequestInput = z.infer<typeof portalChangeRequestSchema>;
export const changeRequestAnswerSchema = z.object({ message: z.string().trim().min(3, 'Napisz odpowiedź dla klienta').max(CHANGE_REQUEST_NOTE_MAX) });

/** Prośba widziana w biurze (zadanie koordynatora „Zmiana terminu na prośbę klienta”). */
export interface ChangeRequestDto extends PortalChangeRequestDto {
  taskId: string;
  taskNumber: string;
  caseId: string;
  resolvedByName: string | null;
}

export interface PortalContractDto {
  id: string;
  caseId: string;
  kind: 'umowa' | 'aneks';
  number: string;
  title: string;
  status: 'do_podpisu' | 'podpisana';
  signedAt: string | null;
  issuedAt: string | null;
  /** podpisany dokument: elektronicznie (zapieczętowany PDF) albo skan podpisu papierowego */
  signedFile: 'epodpis' | 'skan' | null;
}

export interface PortalOfferDto {
  versionId: string;
  caseId: string;
  number: string;
  title: string;
  issuedAt: string | null;
  validUntil: string | null;
  status: 'aktywna' | 'zaakceptowana' | 'nieaktualna';
  grossGr: number | null;
}

/**
 * Dokument finansowy w strefie: faktura wystawiona w Fakturowni (pro forma, zaliczkowa, końcowa, VAT — także do aneksu —
 * i korekta). Bez zamówień (podstawa zaliczek) oraz faktur porzuconych i nieudanych.
 */
/** Protokół odbioru prac w strefie klienta (podpisany albo czekający na podpis klienta). */
export interface PortalProtocolDto {
  id: string;
  caseId: string;
  code: string;
  /** np. „Protokół odbioru — Pompa ciepła” */
  title: string;
  state: 'do_podpisu' | 'podpisany';
  /** droga podpisu: na miejscu, e-podpis, papier */
  signMethod: 'na_miejscu' | 'epodpis' | 'papier';
  /** podpis klienta (podpisany) albo przygotowanie protokołu (czeka) */
  date: string;
  signerName: string;
  /** czeka na e-podpis: link wysłany na adres (zamaskowany) i termin ważności */
  esign: { sentTo: string; expiresAt: string; expired: boolean } | null;
  /** PDF do pobrania: podpisany protokół albo protokół do podpisu */
  pdf: boolean;
  /** skan podpisanego wydruku (podpis papierowy) */
  scan: boolean;
  /** uwagi zgłoszone przez klienta przy odbiorze */
  remarks: string;
}

export interface PortalInvoiceDto {
  id: string;
  /** sprawa faktury (strefa klienta; w liście faktur sprawy — ta sprawa) */
  caseId?: string;
  kind: 'proforma' | 'zaliczkowa' | 'koncowa' | 'vat' | 'korekta';
  /** rodzaj opisany dla klienta, np. „Faktura VAT do aneksu A/2026/001” */
  kindLabel: string;
  number: string;
  issueDate: string | null;
  grossGr: number | null;
  paymentTo: string | null;
  /** opłacona / do zapłaty / po terminie; korekta — bez stanu płatności */
  state: 'oplacona' | 'do_zaplaty' | 'po_terminie' | 'korekta';
  /** do zapłaty (gdy state = do_zaplaty / po_terminie) */
  toPayGr: number | null;
  /** dane do przelewu — jak w e-mailu z fakturą; null, gdy nic nie trzeba wpłacać */
  transfer: { account: string; recipient: string; title: string } | null;
  /** przycisk „Zapłać online” (ING Pay): firma ma aktywną integrację, dokument do zapłaty (pro forma, końcowa, VAT) */
  payOnline?: boolean;
  /** korekta: numer faktury korygowanej */
  correctedNumber: string | null;
  /** zaliczkowa: pro forma, którą zastępuje (pro forma w pełni zastąpiona nie jest już na liście) */
  proformaNumber: string | null;
}

/** Sprawa klienta w strefie: numer, czego dotyczy i gdzie, czy trwa. */
export interface PortalCaseDto {
  id: string;
  number: string;
  /** czego dotyczy (tytuł sprawy) */
  title: string;
  /** branże zakresów sprawy */
  trades: string[];
  siteAddress: string;
  closed: boolean;
  /** prace sprawy jeszcze bez terminu (liczba) */
  unscheduled: number;
}

export interface PortalDto {
  /** klient (kartoteka) — strefa pokazuje wszystkie jego sprawy */
  clientName: string;
  /** sprawy klienta: najpierw trwające, potem zakończone (najnowsze pierwsze) */
  cases: PortalCaseDto[];
  company: { name: string; phone: string; email: string; color: string };
  /** opiekun najnowszej trwającej sprawy — kontakt w sprawie terminów */
  contact: { name: string; phone: string; email: string } | null;
  works: PortalWorkDto[];
  contracts: PortalContractDto[];
  offers: PortalOfferDto[];
  /** dokumenty finansowe (faktury) firm, których umowy są w strefie */
  invoices: PortalInvoiceDto[];
  /** protokoły odbioru prac (starsze serwery nie wysyłają) */
  protocols?: PortalProtocolDto[];
  /** odpowiedzi biura w Wiadomościach, których klient jeszcze nie widział (tylko strona klienta) */
  unreadMessages?: number;
  demo: boolean;
}

/** Odpowiedź strony klienta: zaufane urządzenie — dane; inaczej prośba o kod. */
export type PortalViewDto = { state: 'kod'; emailMasked: string; companyName: string; demo: boolean } | { state: 'ok'; portal: PortalDto };

/** Stan strefy klienta (biuro: karta klienta i karta w sprawie). */
export interface PortalStatusDto {
  partyId: string;
  partyName: string;
  active: { id: string; recipientEmail: string; createdAt: string; createdByName: string | null; viewCount: number; lastViewedAt: string | null } | null;
  /** domyślny adres (pierwszy e-mail klienta, w sprawie — płatnik umowy / klient sprawy) */
  suggestedEmail: string | null;
  /** firma nadawcy e-maila z linkiem (wybór przy wysyłce; domyślnie firma ostatniej umowy klienta) */
  companies: { id: string; name: string }[];
  suggestedCompanyId: string | null;
  canSend: boolean;
}

export const portalSendSchema = z.object({ email: z.string().trim().email('Podaj poprawny adres e-mail'), companyId: z.string().uuid().nullable().default(null) });
export type PortalSendInput = z.infer<typeof portalSendSchema>;

/** Dni pracy odcinka (z pominięciem weekendów, gdy ustawiono). */
export function portalWorkDays(w: Pick<PortalWorkDto, 'dateFrom' | 'dateTo' | 'skipWeekends'>): string[] {
  const out: string[] = [];
  const end = new Date(`${w.dateTo}T12:00:00Z`);
  for (let d = new Date(`${w.dateFrom}T12:00:00Z`); d <= end && out.length < 400; d.setUTCDate(d.getUTCDate() + 1)) {
    const wd = d.getUTCDay();
    if (w.skipWeekends && (wd === 0 || wd === 6) && w.dateFrom !== w.dateTo) continue;
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}
